import { ArrowUpRight, Mail, MessageCircle } from "lucide-react";
import { WHATSAPP_LINK, RADAGENT_URL, RADSCRIBE_URL } from "@/lib/constants";

export default function Footer() {
  return (
    <footer
      id="contact"
      data-testid="site-footer"
      className="relative bg-[#071B34] text-white overflow-hidden"
    >
      {/* Top accent line */}
      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#2EE8FF]/60 to-transparent" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-1 bg-[#14D6C7]/40 blur-md" />

      {/* Background pattern */}
      <div
        className="absolute inset-0 opacity-30 pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(20,214,199,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(20,214,199,0.05) 1px, transparent 1px)",
          backgroundSize: "80px 80px",
          maskImage: "radial-gradient(ellipse at top, black 30%, transparent 80%)",
          WebkitMaskImage: "radial-gradient(ellipse at top, black 30%, transparent 80%)",
        }}
        aria-hidden="true"
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-10">
        {/* CTA strip */}
        <div className="flex flex-col lg:flex-row items-start lg:items-end justify-between gap-10 pb-14 border-b border-white/10">
          <div className="max-w-2xl">
            <img src="/logo.png" height={200} width={500} className="w-[25%] mb-6" />
            <h3 className="text-balance text-3xl sm:text-4xl font-manrope font-bold tracking-tight leading-[1.1]">
              Intelligence Infrastructure for Better Healthcare.
            </h3>
            <p className="mt-4 text-white/55 font-poppins text-sm sm:text-base max-w-xl leading-relaxed">
              Building AI-powered systems that help healthcare providers deliver faster
              diagnoses and better patient outcomes.
            </p>
          </div>

          <a
            data-testid="footer-book-demo-btn"
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-shine group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#14D6C7] text-[#071B34] text-sm font-semibold hover:bg-[#2EE8FF] transition-colors duration-300 shadow-[0_0_0_1px_rgba(46,232,255,0.25),0_10px_40px_-10px_rgba(20,214,199,0.55)] font-manrope flex-shrink-0"
          >
            <MessageCircle className="w-4 h-4" strokeWidth={2.2} />
            Book a Demo
            <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2.2} />
          </a>
        </div>

        {/* Link grid */}
        <div className="grid grid-cols-2 lg:grid-cols-12 gap-10 py-14">
          <div className="col-span-2 lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.22em] text-[#14D6C7]/80 font-manrope font-bold mb-4">
              Get in touch
            </div>
            <a
              data-testid="footer-whatsapp-link"
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-3 text-white hover:text-[#14D6C7] transition-colors"
            >
              <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-white/5 border border-white/10 group-hover:border-[#14D6C7]/40 transition-colors">
                <MessageCircle className="w-4 h-4" strokeWidth={1.8} />
              </span>
              <div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-white/45 font-poppins">WhatsApp</div>
                <div className="font-manrope font-semibold">+91 92778 00469</div>
              </div>
            </a>
          </div>

          <div className="lg:col-span-3">
            <div className="text-xs uppercase tracking-[0.22em] text-[#14D6C7]/80 font-manrope font-bold mb-4">
              Products
            </div>
            <ul className="space-y-3 font-poppins text-sm">
              <li>
                <a
                  data-testid="footer-radagent-link"
                  href={RADAGENT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link-underline text-white/75 hover:text-white inline-flex items-center gap-1.5"
                >
                  RadAgent <ArrowUpRight className="w-3.5 h-3.5" />
                </a>
              </li>
              <li>
                <a
                  data-testid="footer-radscribe-link"
                  href={RADSCRIBE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link-underline text-white/75 hover:text-white inline-flex items-center gap-1.5"
                >
                  RadScribe <ArrowUpRight className="w-3.5 h-3.5" />
                </a>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-2">
            <div className="text-xs uppercase tracking-[0.22em] text-[#14D6C7]/80 font-manrope font-bold mb-4">
              Company
            </div>
            <ul className="space-y-3 font-poppins text-sm">
              <li>
                <a
                  data-testid="footer-about-link"
                  href="#why"
                  className="link-underline text-white/75 hover:text-white"
                >
                  About
                </a>
              </li>
              <li>
                <a
                  data-testid="footer-contact-link"
                  href={WHATSAPP_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link-underline text-white/75 hover:text-white"
                >
                  Contact
                </a>
              </li>
            </ul>
          </div>

          <div className="col-span-2 lg:col-span-2">
            <div className="text-xs uppercase tracking-[0.22em] text-[#14D6C7]/80 font-manrope font-bold mb-4">
              Status
            </div>
            <div className="inline-flex items-center gap-2 text-sm font-poppins text-white/70">
              <span className="pulse-dot" />
              All systems intelligent
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-8 border-t border-white/10 text-xs font-poppins text-white/40">
          <div>© 2026 Zeltaa. All rights reserved.</div>
          <div className="flex items-center gap-6">
            <span>Built for clinicians, by clinicians.</span>
          </div>
        </div>
      </div>

      {/* Giant ZELTAA wordmark watermark */}
      <div
        aria-hidden="true"
        className="relative select-none pb-2 px-6 lg:px-10 overflow-hidden"
      >
        <div className="max-w-7xl mx-auto">
          <div
            className="font-manrope font-extrabold tracking-[0.02em] leading-none bg-gradient-to-b from-white/[0.06] to-transparent bg-clip-text text-transparent"
            style={{ fontSize: "clamp(4rem, 18vw, 18rem)" }}
          >
            ZELTAA
          </div>
        </div>
      </div>
    </footer>
  );
}
